import type { ComponentPropsWithoutRef, ComponentRef, ReactNode, Ref } from 'react'
import * as SwitchPrimitive from '@radix-ui/react-switch'

import LogoutIcon from '../../assets/icons/logout-icon'
import SwitchCheckIcon from '../../assets/icons/switch-check-icon'

import { cn } from '../../lib/utils'
import { TooltipStyledPopover } from './popover'

interface SwitchProps extends ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> {
    id: string
    label?: ReactNode | string
    children?: ReactNode
    labelClassName?: string
    rootClassName?: string
    tooltip?: string
    ref?: Ref<ComponentRef<typeof SwitchPrimitive.Root>>
}

export function Switch({
    label,
    children,
    className,
    id,
    labelClassName,
    tooltip,
    rootClassName,
    disabled,
    ref,
    ...props
}: SwitchProps) {
    return (
        <div className={cn('flex items-start', rootClassName)}>
            <SwitchPrimitive.Root
                className={cn(
                    'relative inline-flex items-center w-[46px] h-6 rounded-full cursor-pointer border-none bg-lp-dark-label data-[state=checked]:bg-lp-dark1 disabled:opacity-50 disabled:cursor-not-allowed',
                    className,
                )}
                id={id}
                disabled={disabled}
                ref={ref}
                {...props}
            >
                <SwitchPrimitive.Thumb
                    className="flex justify-center w-[19px] h-[19px] bg-white rounded-full transition-transform duration-100 data-[state=checked]:translate-x-[19px] data-[state=unchecked]:translate-x-[2px]"
                    style={{
                        boxShadow: '0 2px 2px rgba(0, 0, 0, 0.1)',
                        willChange: 'transform',
                        cursor: 'pointer',
                    }}
                >
                    <div
                        style={{
                            position: 'relative',
                            opacity: disabled ? 0.5 : 1,
                            cursor: disabled ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {props.checked ? (
                            <div
                                style={{
                                    paddingTop: '6px',
                                }}
                            >
                                <SwitchCheckIcon />
                            </div>
                        ) : (
                            <div
                                style={{
                                    paddingTop: '1px',
                                    paddingLeft: '0px',
                                }}
                            >
                                <div
                                    style={{
                                        transform: 'scale(0.9)',
                                    }}
                                >
                                    <LogoutIcon />
                                </div>
                            </div>
                        )}
                    </div>
                </SwitchPrimitive.Thumb>
            </SwitchPrimitive.Root>

            <div className="flex w-full items-center justify-between">
                {children ? (
                    <div
                        className={cn(
                            'pl-4',
                            disabled && 'cursor-not-allowed opacity-50',
                            !disabled && 'cursor-pointer',
                            labelClassName,
                        )}
                    >
                        {children}
                    </div>
                ) : label ? (
                    <label
                        htmlFor={id}
                        className={cn(
                            'pl-4 text-sm font-normal text-lp-dark1',
                            disabled && 'cursor-not-allowed opacity-50',
                            !disabled && 'cursor-pointer',
                            labelClassName,
                        )}
                        style={{ lineHeight: '21px' }}
                        dangerouslySetInnerHTML={
                            typeof label === 'string' ? { __html: label } : undefined
                        }
                    >
                        {typeof label !== 'string' ? label : undefined}
                    </label>
                ) : null}
                {tooltip && (
                    <TooltipStyledPopover alt={tooltip}>
                        <p className="text-sm" style={{ color: '#F0F3F8' }}>
                            {tooltip}
                        </p>
                    </TooltipStyledPopover>
                )}
            </div>
        </div>
    )
}
